import { navigate } from 'gatsby';
import React, { useCallback, useEffect, useState } from 'react';
import AutoSuggest from 'react-autosuggest';
import { Translation } from 'react-i18next';
import { Highlight, connectAutoComplete } from 'react-instantsearch-dom';
import { formatSlug } from '../../../../utils/utils';
import type { AutocompleteProps } from './types';

const AutoComplete = ({ currentRefinement, refine, hits }: AutocompleteProps) => {
    const [, setQuery] = useState('');
    const [value, setValue] = useState(currentRefinement);
    const [hitList, setHitList] = useState([]);

    useEffect(() => {
        if (hits.length > 3) {
            const array = hits.slice(0, 4);
            setHitList(array);
        } else {
            setHitList(hits);
        }
    }, [hits]);

    const onSuggestionSelected = useCallback((_, { suggestion }) => {
        const link = formatSlug(suggestion.slug, suggestion.locale);
        navigate(`/${link}`);
    }, []);

    const onSuggestionCleared = useCallback(() => {
        setQuery('');
    }, []);

    const onChange = useCallback(
        (_, { newValue }) => {
            if (!newValue) {
                onSuggestionCleared();
            }
            setValue(newValue);
        },
        [onSuggestionCleared],
    );

    const onSuggestionsFetchRequested = useCallback(
        ({ value: data }) => {
            refine(data);
        },
        [refine],
    );

    const onSuggestionsClearRequested = useCallback(() => {
        refine();
    }, [refine]);

    const getSuggestionValue = useCallback((hit) => hit.title, []);

    const renderSuggestion = useCallback((hit: unknown) => <Highlight attribute="title" hit={hit} tagName="mark" />, []);

    const inputProps = {
        onChange,
        value,
    };

    return (
        <Translation>
            {(t) => (
                <AutoSuggest
                    suggestions={hitList}
                    onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                    onSuggestionsClearRequested={onSuggestionsClearRequested}
                    onSuggestionSelected={onSuggestionSelected}
                    getSuggestionValue={getSuggestionValue}
                    renderSuggestion={renderSuggestion}
                    inputProps={{
                        className: 'p-3 min-w-full rounded-lg focus:outline-none input border-gray-200 border',
                        ...inputProps,
                        placeholder: t('home.search.placeholder', 'Search for a post...'),
                    }}
                />
            )}
        </Translation>
    );
};

export default connectAutoComplete(AutoComplete);
